import { useEffect, useMemo, useRef, useState } from "react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import svg from "../../utils/svg"
import { formatCurrency } from "../../utils/utils"
import styles from "./OffscreenPaywall.module.scss"

type PlanKey = "yearly" | "monthly"
type StageState = "exposed" | "secured"

/**
 * OFFSCREEN — a feature-specific paywall.
 *
 * Concept: the single most provable thing Private Photo Vault does is take a
 * sensitive photo *out* of your Camera Roll and lock it away. This paywall
 * makes that mechanic the hero: a strip of "memories" sits exposed in the
 * camera roll, and the private ones physically lift out and drop into the
 * vault, where they lock. A toggle lets you feel the contrast — what anyone
 * holding your phone can see, versus what only you can open.
 *
 * Behavioural spine: contrast framing + quiet ownership (not fear).
 */

interface Memory {
  id: string
  private: boolean
  video?: boolean
  hue: [string, string]
  motif: "trip" | "portrait" | "doc" | "message" | "night"
  // roll geometry (px, relative to the stage box)
  x: number
  y: number
  rot: number
}

// Stage geometry — fixed so the drop choreography is deterministic across
// widths. The stage is centered and scales down on very small screens via CSS.
const STAGE_W = 320
const CARD = 58
// where cards fall into: the mouth of the vault
const SLOT_X = STAGE_W / 2
const SLOT_Y = 96

const MEMORIES: Memory[] = [
  {
    id: "trip",
    private: false,
    hue: ["#ffd48a", "#ff9d5c"],
    motif: "trip",
    x: 6,
    y: 22,
    rot: -8,
  },
  {
    id: "doc",
    private: true,
    hue: ["#a8c0ff", "#5b7ad2"],
    motif: "doc",
    x: 66,
    y: 3,
    rot: 5,
  },
  {
    id: "portrait",
    private: true,
    video: true,
    hue: ["#ffb1c9", "#e56a94"],
    motif: "portrait",
    x: 126,
    y: 20,
    rot: -3,
  },
  {
    id: "message",
    private: true,
    hue: ["#b8f2d8", "#3fb28a"],
    motif: "message",
    x: 186,
    y: 2,
    rot: 7,
  },
  {
    id: "night",
    private: false,
    hue: ["#c9b8ff", "#7c5cff"],
    motif: "night",
    x: 246,
    y: 24,
    rot: 9,
  },
]

const Motif = ({ kind }: { kind: Memory["motif"] }) => {
  switch (kind) {
    case "trip":
      return (
        <svg viewBox="0 0 74 74" className={styles.motif} aria-hidden>
          <circle cx="50" cy="26" r="11" fill="#fff" opacity="0.85" />
          <path
            d="M0 60 L20 40 L34 52 L52 30 L74 52 L74 74 L0 74 Z"
            fill="#fff"
            opacity="0.35"
          />
        </svg>
      )
    case "portrait":
      return (
        <svg viewBox="0 0 74 74" className={styles.motif} aria-hidden>
          <circle cx="37" cy="30" r="13" fill="#fff" opacity="0.85" />
          <path
            d="M13 74 C13 54 27 46 37 46 C47 46 61 54 61 74 Z"
            fill="#fff"
            opacity="0.5"
          />
        </svg>
      )
    case "doc":
      return (
        <svg viewBox="0 0 74 74" className={styles.motif} aria-hidden>
          <rect
            x="20"
            y="16"
            width="34"
            height="42"
            rx="4"
            fill="#fff"
            opacity="0.85"
          />
          <rect
            x="26"
            y="24"
            width="14"
            height="14"
            rx="7"
            fill="#5b7ad2"
            opacity="0.7"
          />
          <rect
            x="26"
            y="44"
            width="22"
            height="3"
            rx="1.5"
            fill="#5b7ad2"
            opacity="0.6"
          />
          <rect
            x="26"
            y="50"
            width="16"
            height="3"
            rx="1.5"
            fill="#5b7ad2"
            opacity="0.6"
          />
        </svg>
      )
    case "message":
      return (
        <svg viewBox="0 0 74 74" className={styles.motif} aria-hidden>
          <rect
            x="16"
            y="20"
            width="42"
            height="26"
            rx="8"
            fill="#fff"
            opacity="0.85"
          />
          <path d="M26 46 L26 56 L38 46 Z" fill="#fff" opacity="0.85" />
          <rect
            x="24"
            y="28"
            width="26"
            height="3.5"
            rx="1.75"
            fill="#3fb28a"
            opacity="0.7"
          />
          <rect
            x="24"
            y="35"
            width="18"
            height="3.5"
            rx="1.75"
            fill="#3fb28a"
            opacity="0.7"
          />
        </svg>
      )
    case "night":
      return (
        <svg viewBox="0 0 74 74" className={styles.motif} aria-hidden>
          <circle cx="46" cy="26" r="12" fill="#fff" opacity="0.85" />
          <circle cx="40" cy="22" r="12" fill="#7c5cff" opacity="0.9" />
          <circle cx="20" cy="18" r="1.6" fill="#fff" />
          <circle cx="58" cy="46" r="1.6" fill="#fff" />
          <circle cx="30" cy="40" r="1.2" fill="#fff" />
        </svg>
      )
  }
}

const OffscreenPaywall = () => {
  const { state } = useAppState()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const hasTrial = yearlyProduct?.hasTrial ?? true
  const trialDays = yearlyProduct?.introPricing?.period ?? 7

  const savingsPct = useMemo(() => {
    const y = yearlyProduct?.price ?? 35.99
    const m = monthlyProduct?.price ?? 13.99
    return m ? Math.round((1 - y / (m * 12)) * 100) : 0
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<PlanKey>("yearly")
  const [stage, setStage] = useState<StageState>("exposed")
  const didAutoPlay = useRef(false)

  // Auto-play the "secure" choreography once, shortly after mount, so a viewer
  // who never touches the toggle still sees the feature demonstrated.
  useEffect(() => {
    if (didAutoPlay.current) return
    didAutoPlay.current = true
    const t = setTimeout(() => setStage("secured"), 850)
    return () => clearTimeout(t)
  }, [])

  const privateCount = MEMORIES.filter((m) => m.private).length

  const choosePlan = (key: PlanKey) => {
    if (key === plan) return
    setPlan(key)
    native.logMixpanelEvent("html_pricing_toggle", { plan: key })
  }

  const toggleStage = () => {
    setStage((s) => {
      const next = s === "secured" ? "exposed" : "secured"
      native.logMixpanelEvent("html_offscreen_toggle", { state: next })
      return next
    })
  }

  const handleClose = () => native.postNativeEvent("close")

  const purchase = () => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    native.logMixpanelEvent("html_offscreen_purchase", { plan })
    if (product) native.subscribe(product)
  }

  const secured = stage === "secured"

  const perks: {
    icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>
    title: string
    body: string
  }[] = [
    {
      icon: svg.featureIcons.SpaceSaver,
      title: "Off the roll",
      body: "Out of Photos",
    },
    {
      icon: svg.featureIcons.FacedownLock,
      title: "Locked to you",
      body: "Face ID & PIN",
    },
    {
      icon: svg.featureIcons.CloudVault,
      title: "Backed up",
      body: "Off-device copy",
    },
  ]

  return (
    <div className={styles.page}>
      <div className={styles.aurora} aria-hidden />

      <header className={styles.top}>
        <div className={styles.brand}>
          <span className={styles.brandMark} aria-hidden>
            <svg viewBox="0 0 24 24" width="15" height="15">
              <path
                d="M12 2.5 4.5 6v6c0 4.6 3.2 7.9 7.5 9.4 4.3-1.5 7.5-4.8 7.5-9.4V6L12 2.5Z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
              <rect
                x="8.5"
                y="11"
                width="7"
                height="6"
                rx="1.4"
                fill="currentColor"
              />
              <path
                d="M9.6 11V9.3a2.4 2.4 0 0 1 4.8 0V11"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              />
            </svg>
          </span>
          <span className={styles.brandName}>
            Private Photo Vault
            <em>Premium</em>
          </span>
        </div>
        <button
          className={styles.close}
          onClick={handleClose}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
            <path
              d="M6 6l12 12M18 6L6 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      <h1 className={styles.headline}>
        Some photos aren't
        <br />
        for the camera roll.
      </h1>
      <p className={styles.sub}>
        Move the private ones into your vault — out of the Photos app and locked
        behind Face&nbsp;ID. Handing over your phone stops meaning handing over
        everything.
      </p>

      {/* ── Hero: the contrast mechanic ─────────────────────────────── */}
      <div className={styles.stageWrap}>
        <div
          className={`${styles.stage} ${secured ? styles.stageSecured : ""}`}
          style={{ ["--w" as any]: `${STAGE_W}px` }}
        >
          {MEMORIES.map((m, i) => {
            const cx = m.x + CARD / 2
            const cy = m.y + CARD / 2
            const dx = SLOT_X - cx
            const dy = SLOT_Y - cy
            return (
              <div
                key={m.id}
                className={`${styles.card} ${m.private ? styles.cardPrivate : styles.cardSafe}`}
                style={{
                  ["--x" as any]: `${m.x}px`,
                  ["--y" as any]: `${m.y}px`,
                  ["--rot" as any]: `${m.rot}deg`,
                  ["--dx" as any]: `${dx}px`,
                  ["--dy" as any]: `${dy}px`,
                  ["--i" as any]: i,
                  background: `linear-gradient(150deg, ${m.hue[0]}, ${m.hue[1]})`,
                }}
              >
                <Motif kind={m.motif} />
                {m.video && (
                  <span className={styles.videoBadge} aria-hidden>
                    <svg viewBox="0 0 12 12" width="8" height="8">
                      <path d="M3 2l7 4-7 4z" fill="#fff" />
                    </svg>
                  </span>
                )}
                {m.private && (
                  <span className={styles.cardLock} aria-hidden>
                    <svg viewBox="0 0 24 24" width="15" height="15">
                      <rect
                        x="5"
                        y="10.5"
                        width="14"
                        height="10.5"
                        rx="2.4"
                        fill="#fff"
                      />
                      <path
                        d="M7.5 10.5V8a4.5 4.5 0 0 1 9 0v2.5"
                        fill="none"
                        stroke="#fff"
                        strokeWidth="2"
                      />
                    </svg>
                  </span>
                )}
              </div>
            )
          })}

          {/* The vault */}
          <div className={`${styles.vault} ${secured ? styles.vaultFull : ""}`}>
            <div className={styles.vaultSlot} aria-hidden />
            <div className={styles.vaultFace}>
              <span className={styles.vaultId} aria-hidden>
                <svg viewBox="0 0 24 24" width="22" height="22">
                  <rect
                    x="3.5"
                    y="3.5"
                    width="17"
                    height="17"
                    rx="5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    opacity="0.55"
                  />
                  <path
                    d="M8 9.2v-.4M16 9.2v-.4M9 15c1 1.1 5 1.1 6 0"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    fill="none"
                  />
                </svg>
              </span>
              <span className={styles.vaultCount}>
                <strong>{secured ? privateCount : 0}</strong> in your vault
              </span>
            </div>
          </div>
        </div>

        <button
          className={styles.toggle}
          onClick={toggleStage}
          aria-pressed={secured}
        >
          <span
            className={`${styles.toggleOpt} ${!secured ? styles.toggleOn : ""}`}
          >
            Exposed
          </span>
          <span
            className={`${styles.toggleOpt} ${secured ? styles.toggleOn : ""}`}
          >
            In your vault
          </span>
          <span
            className={styles.toggleKnob}
            style={{ ["--knob" as any]: secured ? "1" : "0" }}
            aria-hidden
          />
        </button>
      </div>

      {/* ── What moving to the vault actually does ──────────────────── */}
      <div className={styles.perks}>
        {perks.map((p) => (
          <div className={styles.perk} key={p.title}>
            <span className={styles.perkIcon}>
              <p.icon />
            </span>
            <span className={styles.perkTitle}>{p.title}</span>
            <span className={styles.perkBody}>{p.body}</span>
          </div>
        ))}
      </div>

      {/* ── Pricing: yearly featured, monthly secondary ─────────────── */}
      <div className={styles.pricing}>
        <button
          className={`${styles.yearCard} ${plan === "yearly" ? styles.yearOn : ""}`}
          onClick={() => choosePlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.yearRadio} aria-hidden />
          <div className={styles.yearMain}>
            <span className={styles.yearName}>
              Yearly
              <span className={styles.recommend}>Best value</span>
            </span>
            <span className={styles.yearSub}>
              {yearlyPrice}/year · ≈ {yearlyPerMonth}/mo
            </span>
          </div>
          {savingsPct > 0 && (
            <span className={styles.saveBadge}>
              Save {savingsPct}%<em>vs monthly</em>
            </span>
          )}
        </button>

        <button
          className={`${styles.monthRow} ${plan === "monthly" ? styles.monthOn : ""}`}
          onClick={() => choosePlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.monthRadio} aria-hidden />
          <span className={styles.monthName}>Monthly</span>
          <span className={styles.monthPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <button className={styles.cta} onClick={purchase}>
        <span className={styles.ctaMain}>
          {plan === "yearly" && hasTrial
            ? `Try ${trialDays} Days Free`
            : "Protect My Photos"}
        </span>
        <span className={styles.ctaSub}>
          {plan === "yearly"
            ? hasTrial
              ? `then ${yearlyPrice}/year (≈ ${yearlyPerMonth}/mo)`
              : `${yearlyPrice}/year (≈ ${yearlyPerMonth}/mo)`
            : `${monthlyPrice}/mo`}
        </span>
      </button>

      <p className={styles.reassure}>
        {plan === "yearly" && hasTrial ? "No charge today · " : ""}
        Cancel anytime · Subscriptions auto-renew until canceled
      </p>

      <div className={styles.legal}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>
    </div>
  )
}

export default OffscreenPaywall
